import React from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { useConsultation } from "../../../context/ConsultationContext";

const HERO_IMG =
  "https://images.pexels.com/photos/220444/pexels-photo-220444.jpeg?auto=compress&cs=tinysrgb&w=2400";

const PILLS = [
  "Mainland Setup",
  "Freezone Setup",
  "Offshore",
  "Corporate Banking",
  "Golden Visa",
  "Investor Visa",
  "PRO Services",
  "Accounting & VAT",
];

export default function HeroV2() {
  const { open } = useConsultation();
  return (
    <section
      data-testid="v2-hero"
      className="relative isolate min-h-[88vh] flex flex-col overflow-hidden bg-[#001A4D]"
    >
      {/* Background */}
      <motion.div
        initial={{ scale: 1.08, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0 -z-20"
      >
        <img
          src={HERO_IMG}
          alt="Dubai skyline at night"
          className="w-full h-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#001A4D]/95 via-[#002366]/90 to-[#001438]/97" />
      <div
        className="absolute -bottom-10 left-0 right-0 h-32 bg-gradient-to-t from-[#001A4D] to-transparent -z-10 pointer-events-none"
        aria-hidden
      />

      <div className="container-x relative pt-28 pb-10 md:pt-32 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-wrap items-center gap-3 mb-6"
            >
              <span className="chip-dark">
                <span className="w-1.5 h-1.5 rounded-full bg-[#6B8AFF] animate-pulse" />
                UAE Business Setup Experts
              </span>
              <span className="hidden sm:inline-flex items-center gap-2 text-xs text-slate-300/80">
                <ShieldCheck className="w-3.5 h-3.5 text-[#6B8AFF]" />
                Government-licensed advisors
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-heading font-light tracking-tight text-white text-balance text-3xl sm:text-4xl lg:text-5xl leading-[1.08] max-w-4xl"
              data-testid="v2-hero-headline"
            >
              Business setup advisory with{" "}
              <span className="font-black text-white">expert</span>{" "}
              <span className="font-black italic text-[#6B8AFF]">accounting</span>{" "}
              &{" "}
              <span className="font-black italic text-[#6B8AFF]">compliance</span>.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-6 max-w-2xl text-slate-200/90 text-sm md:text-base leading-relaxed"
            >
              StartINC houses UAE business setup specialists who can handle the
              entire process — from license registration and bank account
              opening to accounting, tax and long-term compliance. Built for
              founders who value speed, transparency and a single point of
              accountability.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <button
                data-testid="v2-hero-cta"
                onClick={() => open("other")}
                className="btn-gold group text-sm md:text-base justify-center"
              >
                Get In Touch
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a href="#jurisdictions" className="btn-outline-gold text-sm md:text-base justify-center">
                Explore Jurisdictions
              </a>
            </motion.div>

            {/* Quick value pills (replacing trust block) */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-x-6 gap-y-2 md:gap-y-3 text-xs md:text-sm text-slate-300/80"
            >
              <span className="inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#6B8AFF]" />
                100% foreign ownership
              </span>
              <span className="hidden sm:inline w-px h-4 bg-white/15" />
              <span className="inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#6B8AFF]" />
                Bank account introductions
              </span>
              <span className="hidden sm:inline w-px h-4 bg-white/15" />
              <span className="inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#6B8AFF]" />
                Transparent, written quotes
              </span>
            </motion.div>
          </div>

          {/* Right glass card */}
          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="lg:col-span-4 hidden lg:block"
          >
            <div className="rounded-sm backdrop-blur-xl bg-white/5 border border-white/10 p-7">
              <p className="overline text-[#6B8AFF]">Free consultation</p>
              <h3 className="mt-4 font-heading font-light text-xl tracking-tight text-white leading-tight">
                20 minutes with a{" "}
                <span className="font-black text-[#6B8AFF]">senior advisor</span>.
              </h3>
              <ul className="mt-5 space-y-2.5 text-sm text-slate-300/90">
                {[
                  "Personalised Mainland vs Freezone roadmap",
                  "Transparent written quote within 24 hours",
                  "Direct introductions to UAE banking RMs",
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="w-1 h-1 mt-2 rounded-full bg-[#6B8AFF] shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
              <button
                data-testid="v2-hero-aside-cta"
                onClick={() => open("other")}
                className="mt-6 btn-gold w-full text-sm"
              >
                Book my call <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.aside>
        </div>
      </div>

      {/* Service pills marquee — sits just below the trust pills */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.7 }}
        className="relative mt-6 md:mt-8 pb-8"
      >
        <Marquee speed={32} gradient={false} pauseOnHover>
          {PILLS.concat(PILLS).map((p, i) => (
            <span
              key={`${p}-${i}`}
              className="mx-3 inline-flex items-center gap-3 px-4 py-2 rounded-full border border-white/15 bg-white/5 text-white/85 text-sm font-heading"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#6B8AFF]" />
              {p}
            </span>
          ))}
        </Marquee>
      </motion.div>
    </section>
  );
}
